'use client';
import React, { useCallback, useEffect, useMemo } from 'react';
import './ImageEditor.css';
import '@pqina/pintura/pintura.css';
import { PinturaEditor } from '@pqina/react-pintura';
import { getEditorDefaults } from '@pqina/pintura';
import {
	LocaleCore as LocaleCoreIT,
	LocaleCrop as LocaleCropIT,
	LocaleFilter as LocaleFilterIT,
	LocaleFinetune as LocaleFinetuneIT,
} from '@pqina/pintura/locale/it_IT';
import {
	LocaleCore as LocaleCoreEN,
	LocaleCrop as LocaleCropEN,
	LocaleFilter as LocaleFilterEN,
	LocaleFinetune as LocaleFinetuneEN,
} from '@pqina/pintura/locale/en_GB';
import {
	LocaleCore as LocaleCoreFR,
	LocaleCrop as LocaleCropFR,
	LocaleFilter as LocaleFilterFR,
	LocaleFinetune as LocaleFinetuneFR,
} from '@pqina/pintura/locale/fr_FR';

import { LocaleCode } from '../../core';
import { useScroll } from '../../hooks';

interface ImageEditorProps {
	src: string | undefined;
	onProcess: (e: any) => void | Promise<void>;
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	layoutDirectionPreference?: 'vertical' | 'auto' | 'horizontal';
	imageCropAspectRatio?: number;
	willRenderCanvas?: (...args: any) => any;
	locale?: LocaleCode;
	t?: (key: string) => string;
}

const ImageEditor = ({
	src,
	onProcess,
	isOpen,
	setIsOpen,
	layoutDirectionPreference = 'vertical',
	imageCropAspectRatio,
	willRenderCanvas,
	locale,
	t,
}: ImageEditorProps) => {
	const { disableScroll, enableScroll } = useScroll();

	useEffect(() => {
		if (isOpen) {
			disableScroll();
		} else {
			enableScroll();
		}
	}, [isOpen]);

	const imageEditorLocale = useMemo(() => {
		const italianLocale = {
			...LocaleCoreIT,
			...LocaleCropIT,
			...LocaleFilterIT,
			...LocaleFinetuneIT,
			labelButtonExport: 'Fatto',
		};
		if (locale != null) {
			if (locale === 'it') {
				return italianLocale;
			} else if (locale === 'en') {
				return {
					...LocaleCoreEN,
					...LocaleCropEN,
					...LocaleFilterEN,
					...LocaleFinetuneEN,
				};
			} else if (locale === 'fr') {
				return {
					...LocaleCoreFR,
					...LocaleCropFR,
					...LocaleFilterFR,
					...LocaleFinetuneFR,
				};
			}
		}
		return italianLocale;
	}, [locale]);

	const onPopState = useCallback(
		(event: PopStateEvent) => {
			if (isOpen) {
				event.preventDefault();
				enableScroll();
				setIsOpen(false);
				window.history.replaceState(null, '');
			}
		},
		[isOpen, setIsOpen],
	);

	useEffect(() => {
		// Ensure that when the back button of the browser is pressed
		// and the editor is open, instead of actually navigating to
		// the previous page, the editor is closed, so that all the
		// edited data in the form of which the editor is a part is
		// not lost by mistake.
		if (isOpen) {
			window.history.pushState(null, '');
			window.addEventListener('popstate', onPopState);
		} else {
			window.removeEventListener('popstate', onPopState);
		}
		return () => {
			enableScroll();
			window.removeEventListener('popstate', onPopState);
		};
	}, [isOpen, onPopState]);

	if (!isOpen) {
		return null;
	}

	return (
		<div className='image-editor__wrapper'>
			<PinturaEditor
				{...getEditorDefaults()}
				locale={imageEditorLocale}
				src={src}
				onProcess={onProcess}
				enableButtonClose={true}
				onClose={() => {
					enableScroll();
					setIsOpen(false);
				}}
				utils={['crop', 'finetune', 'filter']}
				util={'crop'}
				layoutDirectionPreference={layoutDirectionPreference}
				imageCropAspectRatio={imageCropAspectRatio}
				willRenderCanvas={willRenderCanvas}
				willRenderToolbar={(toolbar) => {
					const walk = (nodes: any[]) => {
						for (const node of nodes) {
							if (!Array.isArray(node)) continue;
							if (node[1] === 'close' && node[2]) {
								node[2].hideLabel = false;
								node[2].label = t == null ? 'Esci' : t('Exit');
								node[2].icon = null;
							}
							if (Array.isArray(node[3])) walk(node[3]);
						}
					};
					walk(toolbar);
					return toolbar;
				}}
			/>
		</div>
	);
};

export { ImageEditor };
